/**
 * Custom Element pour la modale "Trouver mon vélo".
 * Flow en 4 questions (musculaire ou électrique) :
 *
 *   intro → bikeType → type → size → budget → lead → result → success
 *   (l'étape "type" garde en interne la clé `terrain` pour compat tracking/retry/n8n)
 *   (l'étape "lead" = coordonnées optionnelles, hors flowOrder, intercalée avant result)
 */

// Mapping bikeType → valeur exacte du filtre Shopify category
const BIKETYPE_CATEGORY = {
  musc: 'Musculaire',
  elec: 'Électrique',
};

// Webhook n8n appelé au clic « Continuer » de l'écran coordonnées (lead).
const LEAD_WEBHOOK_URL = 'https://mintbikes.app.n8n.cloud/webhook/29b74ae5-1d69-41ae-8a88-dd5cf24386fe';

// type de vélo → types Shopify (filtrage via filter.p.m.specs.type)
// Chaque option filtre uniquement sur son ou ses type(s) — pas de chevauchement.
const TERRAIN_TYPES = {
  vtt: ['VTT'],
  ville: ['URBAIN', 'VTC'],
  route: ['ROUTE'],
  gravel: ['GRAVEL'],
};

// Ordre de relâchement des critères quand la recherche initiale ne trouve aucun vélo.
// On garde toujours bikeType et sizeCm (contraintes structurelles).
const RETRY_DROP_ORDER = ['terrain', 'budget'];

// taille cm → catégorie
function cmToSize(cm) {
  if (cm < 158) return 'XS';
  if (cm < 168) return 'S';
  if (cm < 178) return 'M';
  if (cm < 188) return 'L';
  return 'XL';
}

// Quand un filtre URL variant-level est actif (ex. filter.v.m.taille_du_cycliste.liste),
// Shopify ajoute ?variant=<id> à l'href du produit pointant vers la variante matchée.
function extractVariantIdFromHref(href) {
  if (!href) return null;
  const m = href.match(/[?&]variant=(\d+)/);
  return m ? m[1] : null;
}

function getCandidateHref(node) {
  if (!node) return '';
  if (node.tagName === 'A') return node.getAttribute('href') || '';
  return node.querySelector('a[href]')?.getAttribute('href') || '';
}

class AlertBikesModal extends HTMLElement {
  constructor() {
    super();
    this.state = {
      bikeType: null,
      terrain: null,
      size: null,
      sizeCm: null,
      budget: 3000, // défaut = carte budget pré-sélectionnée (1500 € - 3000 €)
      leadEmail: null, // écran coordonnées (optionnel) avant résultats
      leadPhone: null,
    };
    this.allSteps = []; // toutes les .x-step (intro + 4 questions + lead + result + success)
    this.flowOrder = []; // ordre semantic (depend de bikeType)
    this.currentStep = null; // data-step current value
    this.isSubmitting = false;
    this.currentSearchUri = '';
    this.leadSubmitted = false; // écran coordonnées déjà rempli → ne plus le réafficher
    this.leadPhoneIti = null; // instance intl-tel-input du champ téléphone (lead)
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.allSteps = Array.from(this.querySelectorAll('.x-step'));
    this.storefrontToken = this.dataset.storefrontToken || '';
    this.storefrontApiVersion = this.dataset.storefrontApiVersion || '2026-01';
    this.shopDomain = this.dataset.shopDomain || '';
    this.addEventListener('click', this.handleClick);
    this.addEventListener('input', (e) => this.handleInput(e));
    this.addEventListener('keydown', (e) => this.handleKeydown(e));
    this.recomputeFlow();
    this.initLeadPhone();
    this.activateStep('intro');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  // -------------------------------------------------------- flow
  recomputeFlow() {
    this.flowOrder = ['bikeType', 'terrain', 'size', 'budget'];
  }

  flowIndex(stepKey) {
    return this.flowOrder.indexOf(stepKey);
  }

  // Vrai si l'étape existe réellement dans le DOM rendu. Sert de garde-fou : si le
  // snippet Liquid n'a pas (encore) l'étape `lead` (cache/déploiement partiel), on ne
  // veut pas bloquer le tunnel sur un activateStep() qui ne trouve pas sa cible.
  hasStep(stepKey) {
    return this.allSteps.some((s) => s.dataset.step === stepKey);
  }

  // -------------------------------------------------------- navigation
  activateStep(stepKey) {
    const target = this.allSteps.find((s) => s.dataset.step === stepKey);
    if (!target) return;
    this.allSteps.forEach((s) => s.classList.remove('x-stepper-active'));
    target.classList.add('x-stepper-active');
    this.currentStep = stepKey;

    // Desktop : les écrans résultats, succès et expert passent en plein écran.
    // (le mobile est de toute façon fullscreen via media query CSS).
    const fullscreenSteps = ['result', 'success', 'expert'];
    this.classList.toggle('alert-bikes-modal--fullscreen', fullscreenSteps.includes(stepKey));

    this.refreshTopBar();
    this.refreshNextEnabled();

    if (stepKey === 'lead') {
      this.refreshLeadContinueEnabled();
    }

    if (stepKey === 'result') {
      this.searchResults();
      this.refreshAlertSubmitEnabled();
    }
  }

  goNext() {
    if (this.currentStep === 'intro') {
      this.activateStep(this.flowOrder[0]);
      return;
    }
    if (this.currentStep === 'success') return;
    if (this.currentStep === 'result') {
      this.activateStep('success');
      return;
    }
    // Écran coordonnées : intercalé entre la dernière question et les résultats.
    if (this.currentStep === 'lead') {
      this.activateStep('result');
      return;
    }
    const idx = this.flowIndex(this.currentStep);
    if (idx === -1) return;
    if (idx === this.flowOrder.length - 1) {
      // On saute l'écran coordonnées s'il est absent ou déjà rempli.
      const showLead = this.hasStep('lead') && !this.leadSubmitted;
      this.activateStep(showLead ? 'lead' : 'result');
    } else {
      this.activateStep(this.flowOrder[idx + 1]);
    }
  }

  goBack() {
    // Depuis les résultats, « Modifier mes critères » renvoie sur les questions
    // (dernière = budget), pas sur l'écran coordonnées (lead) qui n'est qu'une passerelle.
    if (this.currentStep === 'result' || this.currentStep === 'lead') {
      this.activateStep(this.flowOrder[this.flowOrder.length - 1]);
      return;
    }
    if (this.currentStep === 'intro') return;
    const idx = this.flowIndex(this.currentStep);
    if (idx <= 0) {
      this.activateStep('intro');
      return;
    }
    this.activateStep(this.flowOrder[idx - 1]);
  }

  refreshTopBar() {
    // Expose l'étape courante sur l'hôte → permet au CSS de cibler la page de résultats
    // (ex. masquer le topbar, afficher le bouton de fermeture flottant).
    this.dataset.currentStep = this.currentStep || '';

    const counter = this.querySelector('[data-step-counter]');
    const fill = this.querySelector('[data-progress-fill]');

    const isQuestion = this.flowOrder.includes(this.currentStep);

    if (counter) counter.hidden = !isQuestion;

    if (isQuestion) {
      const idx = this.flowIndex(this.currentStep);
      const total = this.flowOrder.length;
      const cur = this.querySelector('[data-step-current]');
      const tot = this.querySelector('[data-step-total]');
      if (cur) cur.textContent = String(idx + 1);
      if (tot) tot.textContent = String(total);
      if (fill) fill.style.width = `${Math.round(((idx + 1) / total) * 100)}%`;
      // eyebrow text on the active question
      const target = this.allSteps.find((s) => s.dataset.step === this.currentStep);
      if (target) {
        const eb = target.querySelector('[data-eb-text]');
        if (eb) eb.textContent = `Étape ${idx + 1} sur ${total}`;
      }
    } else if (
      this.currentStep === 'lead' ||
      this.currentStep === 'result' ||
      this.currentStep === 'success'
    ) {
      if (fill) fill.style.width = '100%';
    } else {
      if (fill) fill.style.width = '0%';
    }
  }

  refreshNextEnabled() {
    const target = this.allSteps.find((s) => s.dataset.step === this.currentStep);
    if (!target) return;
    const nextBtn = target.querySelector('[data-action="next"]');
    if (!nextBtn) return;
    let enabled = true;
    switch (this.currentStep) {
      case 'bikeType':
        enabled = !!this.state.bikeType;
        break;
      case 'terrain':
        enabled = !!this.state.terrain;
        break;
      case 'size':
        enabled = !!this.state.size;
        break;
      default:
        enabled = true;
    }
    nextBtn.disabled = !enabled;
  }

  // -------------------------------------------------------- click router
  handleClick(event) {
    const t = event.target;

    const closeBtn = t.closest('.alert-bikes-modal__close-button');
    if (closeBtn) {
      event.preventDefault();
      const modal = this.closest('x-modal');
      if (modal && typeof modal.close === 'function') modal.close();
      return;
    }

    const vcard = t.closest('.alert-bikes-modal__vcard[data-for]');
    if (vcard) {
      event.preventDefault();
      this.handleVcardClick(vcard);
      return;
    }

    const action = t.closest('[data-action]');
    if (!action) return;
    event.preventDefault();
    const a = action.getAttribute('data-action');
    if (a === 'next' && !action.disabled) this.goNext();
    else if (a === 'back') this.goBack();
    else if (a === 'lead-continue') this.handleLeadContinue();
    else if (a === 'lead-skip') this.handleLeadSkip();
    else if (a === 'submit-alert') this.handleSubmit('alert', action);
    else if (a === 'restart-search') this.restart();
    else if (a === 'contact-expert') {
      // Mémorise d'où on vient (result ou success) pour le bouton retour.
      this.previousStepBeforeExpert = this.currentStep;
      this.activateStep('expert');
    } else if (a === 'back-from-expert') {
      this.activateStep(this.previousStepBeforeExpert || 'result');
    }
  }

  handleVcardClick(card) {
    const radioId = card.getAttribute('data-for');
    const input = this.querySelector(`#${radioId}`);
    if (!input) return;
    const name = input.name; // "bikeType" | "terrain"
    this.querySelectorAll(`input[name="${name}"]`).forEach((r) => {
      r.checked = false;
      const opt = this.querySelector(`[data-for="${r.id}"]`);
      if (opt) {
        opt.classList.remove('alert-bikes-modal__vcard--selected');
        opt.setAttribute('aria-checked', 'false');
      }
    });
    input.checked = true;
    card.classList.add('alert-bikes-modal__vcard--selected');
    card.setAttribute('aria-checked', 'true');
    this.state[name] = input.value;
    if (name === 'bikeType') this.recomputeFlow();
    // Budget : valeur numérique (vide = « je ne sais pas » → flexible).
    if (name === 'budget') this.state.budget = parseInt(input.value, 10) || 9999;
    this.refreshNextEnabled();
  }

  handleInput(event) {
    const t = event.target;
    if (t.matches('[data-cm-input]')) {
      const v = parseInt(t.value, 10);
      const valid = !isNaN(v) && v >= 140 && v <= 210;
      if (valid) {
        this.state.sizeCm = v;
        this.state.size = cmToSize(v);
      } else {
        this.state.sizeCm = null;
        this.state.size = null;
      }
      this.refreshNextEnabled();
    }
    if (t.type === 'email' && t.name === 'email') this.validateEmail(t);
    if (t.closest('[data-form-type="alert"]')) this.refreshAlertSubmitEnabled();

    // Écran coordonnées : revalide le CTA « Continuer » à chaque frappe (email + téléphone).
    if (t.matches('[data-lead-email]') || t.matches('[data-lead-phone]')) {
      if (t.matches('[data-lead-email]')) this.validateEmail(t);
      if (t.matches('[data-lead-phone]')) {
        // Erreur visuelle seulement si un numéro est saisi mais invalide.
        const invalid = !!t.value.trim() && !this.isLeadPhoneValid();
        t.classList.toggle('alert-bikes-modal__fi--error', invalid);
      }
      this.refreshLeadContinueEnabled();
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Enter') return;
    if (event.target.tagName === 'TEXTAREA') return;
    const target = this.allSteps.find((s) => s.dataset.step === this.currentStep);
    if (!target) return;
    const nextBtn = target.querySelector('[data-action="next"]:not([disabled])');
    if (nextBtn) {
      event.preventDefault();
      nextBtn.click();
    }
  }

  // -------------------------------------------------------- écran coordonnées (lead)
  // Initialise le sélecteur de préfixe international sur le champ téléphone (même
  // config que le reste du site : cf. alerte-site-modal.js / fmd-form). La librairie
  // intl-tel-input est chargée globalement dans layout/theme.liquid.
  initLeadPhone() {
    const phoneInput = this.querySelector('[data-lead-phone]');
    if (!phoneInput || this.leadPhoneIti) return;

    // La lib peut ne pas encore être prête (script `defer`) → on réessaie.
    if (typeof window.intlTelInput === 'undefined') {
      setTimeout(() => this.initLeadPhone(), 100);
      return;
    }

    try {
      this.leadPhoneIti = window.intlTelInput(phoneInput, {
        initialCountry: 'fr',
        countryOrder: ['fr', 'be', 'ch', 'lu'],
        loadUtils: () => import('https://cdn.jsdelivr.net/npm/intl-tel-input@25.14.0/build/js/utils.js'),
        separateDialCode: true,
        formatOnDisplay: true,
        autoPlaceholder: 'aggressive',
        customPlaceholder: (p) => p,
        i18n: { searchPlaceholder: 'Recherche' },
        dropdownContainer: document.body,
      });
      // countrychange n'émet pas d'event `input` → revalider explicitement le CTA.
      phoneInput.addEventListener('countrychange', () => this.refreshLeadContinueEnabled());
      // isValidNumber() dépend de utils.js (chargé async) : revalider quand prêt.
      if (this.leadPhoneIti.promise && typeof this.leadPhoneIti.promise.then === 'function') {
        this.leadPhoneIti.promise.then(() => this.refreshLeadContinueEnabled()).catch(() => {});
      }
    } catch (_) {
      /* fallback silencieux : validation par longueur dans refreshLeadContinueEnabled */
    }
  }

  // Le téléphone est valide si intl-tel-input le confirme ; tant que utils.js n'est pas
  // chargé (isValidNumber renvoie null/false), on retombe sur un contrôle de longueur.
  isLeadPhoneValid() {
    const phoneInput = this.querySelector('[data-lead-phone]');
    if (!phoneInput) return false;
    const digits = (phoneInput.value || '').replace(/\D/g, '');
    if (this.leadPhoneIti && typeof this.leadPhoneIti.isValidNumber === 'function') {
      const v = this.leadPhoneIti.isValidNumber();
      if (v === null || v === undefined) return digits.length >= 8; // utils pas encore prêt
      return !!v;
    }
    return digits.length >= 8;
  }

  // Active « Continuer » quand le téléphone est valide (obligatoire). L'email est
  // facultatif : s'il est renseigné il doit être valide, mais vide est accepté.
  refreshLeadContinueEnabled() {
    const btn = this.querySelector('[data-action="lead-continue"]');
    if (!btn) return;
    const emailInput = this.querySelector('[data-lead-email]');
    const email = emailInput?.value?.trim() || '';

    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const emailOkOrEmpty = email === '' || emailValid;

    btn.disabled = !(this.isLeadPhoneValid() && emailOkOrEmpty);
  }

  // Continue : au moins un des deux champs valide (garde-fou), appelle le webhook n8n
  // puis passe aux résultats. On n'attend pas la réponse pour ne pas retarder l'affichage.
  handleLeadContinue() {
    const emailInput = this.querySelector('[data-lead-email]');
    const phoneInput = this.querySelector('[data-lead-phone]');
    if (!emailInput && !phoneInput) return;
    this.refreshLeadContinueEnabled();
    const btn = this.querySelector('[data-action="lead-continue"]');
    if (btn && btn.disabled) return;

    const email = emailInput ? emailInput.value.trim() : '';
    const phoneRaw = phoneInput ? phoneInput.value.trim() : '';
    const phone = phoneRaw ? (this.leadPhoneIti ? this.leadPhoneIti.getNumber() : phoneRaw) : '';

    this.state.leadEmail = email || null;
    this.state.leadPhone = phone || null;
    this.leadSubmitted = true; // rempli → on ne réaffichera plus cet écran

    // Pré-remplit l'email du formulaire d'alerte des résultats pour ne pas le redemander.
    if (email) {
      const alertEmail = this.querySelector('[data-form-type="alert"] input[name="email"]');
      if (alertEmail && !alertEmail.value) {
        alertEmail.value = email;
        this.refreshAlertSubmitEnabled();
      }
    }

    this.submitLead();
    this.activateStep('result');
  }

  // POST des coordonnées + critères de recherche vers le webhook n8n (fire-and-forget).
  submitLead() {
    const payload = {
      email: this.state.leadEmail || '',
      motor: BIKETYPE_CATEGORY[this.state.bikeType] || '',
      phone: this.state.leadPhone || '',
      type: this.resolveTypes()[0] || '',
      budget: this.state.budget,
      taille: this.state.sizeCm,
    };
    return fetch(LEAD_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {});
  }

  // Ignorer : on n'enregistre rien, pas d'appel webhook, on passe aux résultats.
  handleLeadSkip() {
    this.state.leadEmail = null;
    this.state.leadPhone = null;
    this.activateStep('result');
  }

  // -------------------------------------------------------- shopify search
  // `dropped` est un Set des critères à ignorer pour cette tentative
  // (utilisé par le retry progressif quand 0 résultat).
  resolveTypes(dropped = new Set()) {
    const t = dropped.has('terrain') ? null : this.state.terrain;
    return TERRAIN_TYPES[t] ? [...TERRAIN_TYPES[t]] : [];
  }

  buildSearchUri(dropped = new Set(), { sortByPriceDesc = true } = {}) {
    const types = this.resolveTypes(dropped);
    const cm = this.state.sizeCm || 175;
    const params = [];
    // Catégorie motorisation : Musculaire vs Électrique
    const category = BIKETYPE_CATEGORY[this.state.bikeType];
    if (category) {
      params.push('filter.p.m.specs.category=' + encodeURIComponent(category));
    }
    types.forEach((tp) => params.push('filter.p.m.specs.type=' + encodeURIComponent(tp)));
    params.push('filter.v.m.taille_du_cycliste.liste=' + encodeURIComponent(cm));
    const budget = dropped.has('budget') ? null : this.state.budget;
    if (budget && budget < 9999) {
      params.push('filter.v.price.gte=0');
      params.push('filter.v.price.lte=' + encodeURIComponent(budget));
    }
    params.push('filter.p.m.custom.available=true');
    params.push('filter.v.availability=1');
    if (sortByPriceDesc) params.push('sort_by=price-descending');
    const fmdCtx = this.getFmdContext();
    const collectionPath = fmdCtx.isFmd ? '/collections/tous-nos-velos-reconditionnes-fmd' : '/collections/all';
    return collectionPath + '?' + params.join('&');
  }

  async fetchSearchAttempt(dropped) {
    const uri = this.buildSearchUri(dropped);
    const response = await fetch(uri);
    const html = await response.text();
    // 10 candidats : laisse de la marge pour skipper les produits dont la variante
    // ciblée par la taille (?variant=<id>) est out-of-stock.
    const { products, total } = this.extractProducts(html, 10);
    return { uri, products, total };
  }

  async searchResults() {
    const heroEl = this.querySelector('[data-hero-bike]');
    const seeAllEl = this.querySelector('[data-see-all]');
    const seeAllLabel = this.querySelector('[data-see-all-label]');
    const noResultsEl = this.querySelector('#no-results');
    const loadingEl = this.querySelector('#results-loading');

    if (loadingEl) loadingEl.style.display = 'flex';
    if (heroEl) {
      heroEl.hidden = true;
      heroEl.innerHTML = '';
    }
    if (seeAllEl) seeAllEl.hidden = true;
    if (noResultsEl) noResultsEl.hidden = true;

    try {
      // Ne tenter de retirer que les critères effectivement présents.
      const droppable = RETRY_DROP_ORDER.filter((k) => this.state[k] != null && this.state[k] !== '');

      const dropped = new Set();
      let result = await this.fetchSearchAttempt(dropped);
      result.products = this.filterEncartMedia(result.products);
      let chosenNodes = await this.pickAvailableCandidates(result.products, 3);

      let i = 0;
      while (chosenNodes.length === 0 && i < droppable.length) {
        dropped.add(droppable[i]);
        i += 1;
        result = await this.fetchSearchAttempt(dropped);
        result.products = this.filterEncartMedia(result.products);
        chosenNodes = await this.pickAvailableCandidates(result.products, 3);
      }

      if (loadingEl) loadingEl.style.display = 'none';
      this.currentSearchUri = this.buildSearchUri(dropped, { sortByPriceDesc: false });

      if (chosenNodes.length > 0) {
        // Compte exact via Storefront API (le HTML scrape sous-évalue car limité à 1 page).
        const exactCount = await this.fetchTotalCount(dropped);
        const displayTotal = exactCount.count != null && exactCount.count > 0 ? exactCount.count : result.total;
        const suffix = exactCount.capped ? '+' : '';

        if (heroEl) {
          // Rendre visible AVANT l'insertion : <product-carousel> initialise Flickity dans son
          // connectedCallback ; si le conteneur est encore `hidden`, Flickity mesure une largeur
          // de 0 et les cellules s'effondrent (zone vide).
          heroEl.hidden = false;
          heroEl.appendChild(this.buildResultsCarousel(chosenNodes, this.getFmdContext()));
        }
        if (seeAllEl) {
          let href = this.currentSearchUri;
          const fmdCtx = this.getFmdContext();
          if (fmdCtx.isFmd) {
            if (fmdCtx.handle) href += '&fmd=' + encodeURIComponent(fmdCtx.handle);
            if (fmdCtx.isGeneric) href += '&fmd-generic=1';
          }
          seeAllEl.href = href;
          if (seeAllLabel && displayTotal > 1) {
            seeAllLabel.textContent = `Voir toute la sélection (${displayTotal}${suffix} vélos)`;
          }
          seeAllEl.hidden = false;
        }
      } else {
        if (noResultsEl) noResultsEl.hidden = false;
      }
    } catch (_) {
      if (loadingEl) loadingEl.style.display = 'none';
      if (noResultsEl) noResultsEl.hidden = false;
    }
  }

  extractProducts(html, max = 1) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const els = doc.querySelectorAll('.grid-product, .product-grid-item, [data-product-item]');
    const products = [];
    for (let i = 0; i < Math.min(els.length, max); i++) products.push(els[i].cloneNode(true));
    return { products, total: els.length };
  }

  // Exclut les encarts média (cartes promo/éditoriales insérées dans la grille) : ce ne sont
  // pas de vrais produits. On retire les nœuds qui portent la classe `encart-media` ou qui en
  // contiennent un.
  filterEncartMedia(nodes) {
    return (nodes || []).filter(
      (node) => !node.classList.contains('encart-media') && !node.querySelector('.encart-media'),
    );
  }

  // Renvoie le nombre exact de vélos qui matchent les filtres, via Storefront GraphQL.
  // Comme le `type` peut mapper sur plusieurs valeurs (ex. terrain=city → URBAIN OU VTC)
  // et que MetafieldFilter ne supporte pas le OR natif, on lance N requêtes en parallèle
  // et on dédupe les product IDs via un Set. Cap à 250 (limite Shopify `first`).
  // Retourne `{ count: number|null, capped: boolean }` — count=null si erreur (l'appelant
  // retombera alors sur le total HTML).
  async fetchTotalCount(dropped) {
    if (!this.storefrontToken || !this.shopDomain) return { count: null, capped: false };

    try {
      const fmdCtx = this.getFmdContext();
      const handle = fmdCtx.isFmd ? 'tous-nos-velos-reconditionnes-fmd' : 'all';
      const types = this.resolveTypes(dropped);
      const baseFilters = this.buildBaseGraphqlFilters(dropped);

      // 1 requête sans filtre type si types vide, sinon 1 requête par type (OR émulé).
      const typeBuckets = types.length > 0 ? types : [null];

      const url = `https://${this.shopDomain}/api/${this.storefrontApiVersion}/graphql.json`;
      const query = `
        query($handle: String!, $filters: [ProductFilter!]) {
          collection(handle: $handle) {
            products(first: 250, filters: $filters) {
              nodes { id }
            }
          }
        }
      `;

      const requests = typeBuckets.map((tp) => {
        const filters = tp
          ? [...baseFilters, { productMetafield: { namespace: 'specs', key: 'type', value: tp } }]
          : baseFilters;
        return fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken,
          },
          body: JSON.stringify({ query, variables: { handle, filters } }),
        }).then((r) => r.json());
      });

      const responses = await Promise.all(requests);
      const ids = new Set();
      for (const json of responses) {
        const nodes = json?.data?.collection?.products?.nodes || [];
        for (const n of nodes) ids.add(n.id);
      }

      const capped = ids.size >= 250;
      return { count: capped ? 250 : ids.size, capped };
    } catch (_) {
      return { count: null, capped: false };
    }
  }

  // Filtres GraphQL communs à toutes les sous-requêtes de fetchTotalCount,
  // dérivés de l'état + dropped. Le filtre `type` est ajouté à part (cf. fetchTotalCount).
  buildBaseGraphqlFilters(dropped) {
    const filters = [];
    const category = BIKETYPE_CATEGORY[this.state.bikeType];
    if (category) {
      filters.push({ productMetafield: { namespace: 'specs', key: 'category', value: category } });
    }
    filters.push({ productMetafield: { namespace: 'custom', key: 'available', value: 'true' } });
    filters.push({ available: true });
    const cm = this.state.sizeCm || 175;
    filters.push({
      variantMetafield: { namespace: 'taille_du_cycliste', key: 'liste', value: String(cm) },
    });
    const budget = dropped.has('budget') ? null : this.state.budget;
    if (budget && budget < 9999) {
      filters.push({ price: { min: 0, max: budget } });
    }
    return filters;
  }

  // Le filtre URL collection est au niveau produit : un produit passe si AU MOINS UNE
  // variante a le bon cm ET AU MOINS UNE est en stock — pas forcément la même.
  // Shopify met `?variant=<id>` dans le href du produit pointant vers la variante
  // qui matche le filtre cm. On vérifie sa dispo réelle via Storefront GraphQL.
  // Renvoie le 1er node DOM dont la variante ciblée est dispo (ou n'a pas de variant=
  // dans l'href, auquel cas le filtre availability=1 produit suffit). null si tous OOS.
  // Renvoie jusqu'à `limit` cartes produit dont la variante ciblée (via ?variant=) est
  // réellement disponible. Si on ne peut pas vérifier (pas de variant-id / pas de token /
  // erreur réseau), on retombe sur les premières cartes pour ne pas bloquer l'affichage.
  async pickAvailableCandidates(productNodes, limit = 3) {
    if (!productNodes || !productNodes.length) return [];

    const items = productNodes.map((node) => ({
      node,
      variantId: extractVariantIdFromHref(getCandidateHref(node)),
    }));
    const variantIds = [...new Set(items.map((i) => i.variantId).filter(Boolean))];

    if (!variantIds.length || !this.storefrontToken || !this.shopDomain) {
      return items.slice(0, limit).map((i) => i.node);
    }

    const url = `https://${this.shopDomain}/api/${this.storefrontApiVersion}/graphql.json`;
    const gids = variantIds.map((id) => `gid://shopify/ProductVariant/${id}`);
    const query = `
      query($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on ProductVariant { id availableForSale }
        }
      }
    `;
    const availabilityById = new Map();
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({ query, variables: { ids: gids } }),
      });
      const json = await res.json();
      for (const n of json?.data?.nodes || []) {
        if (n?.id) {
          const numericId = String(n.id).split('/').pop();
          availabilityById.set(numericId, !!n.availableForSale);
        }
      }
    } catch (_) {
      return items.slice(0, limit).map((i) => i.node);
    }

    const chosen = [];
    for (const { node, variantId } of items) {
      // Pas de variant-id ciblé sur ce candidat → pas de filtre variant-level, considéré dispo.
      if (!variantId || availabilityById.get(variantId)) {
        chosen.push(node);
        if (chosen.length >= limit) break;
      }
    }
    return chosen;
  }

  // Réécrit les liens produit d'une carte clonée pour le contexte FMD (parcours entreprise).
  applyFmdToLinks(node, fmdCtx) {
    if (!fmdCtx || !fmdCtx.isFmd) return;
    node.querySelectorAll('a[href*="/products/"]').forEach((a) => {
      const href = a.getAttribute('href');
      if (!href) return;
      const sep = href.includes('?') ? '&' : '?';
      let fmdParams = 'view=parcours-fmd';
      if (fmdCtx.handle) fmdParams += '&fmd=' + encodeURIComponent(fmdCtx.handle);
      if (fmdCtx.isGeneric) fmdParams += '&fmd-generic=1';
      a.setAttribute('href', href + sep + fmdParams);
    });
  }

  // Construit un <product-carousel> (Flickity) à partir des cartes produit serveur (.grid-product)
  // déjà rendues par la collection : on réutilise tel quel le markup natif (badges, prix, location)
  // au lieu de le reconstruire. 1 carte visible + aperçu de la suivante (largeur de slide via CSS).
  // Prépare une carte produit clonée pour l'affichage dans la modale.
  prepareCard(node, fmdCtx) {
    // AOS masque les cartes hors scroll-trigger : on neutralise dans la modale.
    node.removeAttribute('data-aos');
    node.querySelectorAll('[data-aos]').forEach((el) => el.removeAttribute('data-aos'));
    // Forcer le chargement des images : lazysizes ne traite pas de façon fiable des
    // nœuds clonés via DOMParser, et `.lazyload` peut rester en opacity:0.
    // ⚠️ `data-src` contient un placeholder `{width}` (ex. ..._{width}x.jpg) que lazysizes
    // remplace normalement ; on doit le résoudre nous-mêmes, sinon l'URL est invalide.
    node.querySelectorAll('img[data-src]').forEach((img) => {
      const raw = img.getAttribute('data-src') || '';
      img.setAttribute('src', raw.replace('{width}', '720'));
      // srcset responsive à partir de data-widths (mêmes placeholders {width}).
      const widthsAttr = img.getAttribute('data-widths');
      if (raw.includes('{width}') && widthsAttr) {
        try {
          const srcset = JSON.parse(widthsAttr)
            .map((w) => `${raw.replace('{width}', w)} ${w}w`)
            .join(', ');
          img.setAttribute('srcset', srcset);
          if (!img.getAttribute('sizes')) img.setAttribute('sizes', '(min-width: 768px) 600px, 90vw');
        } catch (_) {
          /* data-widths illisible : on garde juste le src 720 */
        }
      }
      img.removeAttribute('data-src');
      img.removeAttribute('data-widths');
      img.removeAttribute('data-sizes');
      img.classList.remove('lazyload');
      img.classList.add('lazyloaded');
      img.setAttribute('loading', 'eager');
    });
    this.applyFmdToLinks(node, fmdCtx);
    return node;
  }

  buildResultsCarousel(nodes, fmdCtx) {
    // Sécurité : on n'affiche que les 3 premiers produits.
    const cards = nodes.slice(0, 3);

    // Un seul résultat : pas de carrousel (flèches/points inutiles), juste la carte produit.
    if (cards.length === 1) {
      const single = document.createElement('div');
      single.className = 'alert-bikes-modal__results-single';
      single.appendChild(this.prepareCard(cards[0], fmdCtx));
      return single;
    }

    const carousel = document.createElement('product-carousel');
    carousel.className = 'product-carousel__wrapper alert-bikes-modal__results-carousel';
    carousel.dataset.productsPerSlide = '1';
    carousel.dataset.showArrows = 'true';
    carousel.dataset.showDots = 'true';
    carousel.dataset.autoplay = 'false';
    // Carte active centrée (peek de la précédente ET de la suivante au milieu) ; `contain`
    // colle quand même la 1ère/dernière carte aux bords.
    carousel.dataset.cellAlign = 'center';
    // Pas de boucle infinie : avec seulement 3 cartes, wrapAround les duplique (clones) et donne
    // l'impression d'afficher « tous les résultats ». On veut un carrousel fini de 3 cartes.
    carousel.dataset.wrapAround = 'false';

    const trackContainer = document.createElement('div');
    trackContainer.className = 'product-carousel__track-container';
    const track = document.createElement('div');
    track.className = 'product-carousel__track';

    cards.forEach((node) => {
      const slide = document.createElement('div');
      slide.className = 'product-carousel__slide';
      slide.appendChild(this.prepareCard(node, fmdCtx));
      track.appendChild(slide);
    });

    trackContainer.appendChild(track);
    carousel.appendChild(trackContainer);

    // Flèches : même icône (gauche) pour les deux ; le CSS retourne `--next` de 180°.
    const arrowSvg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none"><path d="M2.91626 10H17.9163M2.91626 10L7.91626 15M2.91626 10L7.91626 5" stroke="#141414" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const nav = document.createElement('div');
    nav.className = 'product-carousel__navigation';
    nav.innerHTML =
      '<button class="product-carousel__arrow product-carousel__arrow--prev" type="button" aria-label="Précédent">' +
      arrowSvg +
      '</button>' +
      '<button class="product-carousel__arrow product-carousel__arrow--next" type="button" aria-label="Suivant">' +
      arrowSvg +
      '</button>';
    carousel.appendChild(nav);

    // <product-carousel> s'initialise via connectedCallback à l'insertion. On recale la hauteur
    // (adaptiveHeight) une fois les images chargées / au frame suivant, sans détruire l'instance.
    const nudge = () => {
      if (carousel.flickity) carousel.flickity.resize();
    };
    requestAnimationFrame(nudge);
    carousel.addEventListener('load', nudge, true);
    setTimeout(nudge, 400);

    return carousel;
  }

  // -------------------------------------------------------- submit alerte email
  async handleSubmit(formType, trigger) {
    if (this.isSubmitting) return;

    // Cible le formulaire qui contient réellement le bouton cliqué (le markup peut avoir
    // plusieurs `.alert-bikes-modal__alert-form` ; on ne dépend pas d'un id potentiellement dupliqué).
    const form =
      (trigger && trigger.closest('.alert-bikes-modal__alert-form')) ||
      this.querySelector('[data-form-type="alert"]');
    if (!form) return;

    const emailInput = form.querySelector('input[name="email"]');
    const firstnameInput = form.querySelector('input[name="firstname"]');
    const lastnameInput = form.querySelector('input[name="lastname"]');

    const errors = [];
    if (!firstnameInput?.value?.trim()) errors.push('firstname');
    if (!lastnameInput?.value?.trim()) errors.push('lastname');
    if (!emailInput?.value?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.value)) {
      errors.push('email');
    }

    if (errors.length > 0) {
      const first = errors[0];
      const el = form.querySelector(`input[name="${first}"]`);
      if (el) {
        el.classList.add('alert-bikes-modal__fi--error');
        el.focus();
      }
      return;
    }

    this.isSubmitting = true;
    const submitBtn = this.querySelector('.alert-bikes-modal__submit');
    if (submitBtn) submitBtn.disabled = true;

    try {
      // ID alerte unique généré côté client (même format que alerte-site-modal.js).
      const ts = Date.now().toString(36);
      const rand = Math.random().toString(36).substring(2, 10);
      const data = {
        id: `alrt_${ts}_${rand}`,
        firstname: firstnameInput?.value || '',
        lastname: lastnameInput?.value || '',
        email: emailInput?.value || '',
      };

      await this.submitToAPI(data);
      this.dispatchEvent(new CustomEvent(`${formType}-form-submitted`, { detail: data, bubbles: true }));
      this.activateStep('success');
    } catch (e) {
      alert('Une erreur est survenue. Veuillez réessayer.');
      console.error(e);
    } finally {
      this.isSubmitting = false;
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  async submitToAPI(data) {
    const apiUrl = 'https://mintbikes.app.n8n.cloud/webhook/alerte_site';

    // Construit `params` au format attendu par n8n (cf. alerte-site-modal.js).
    // Clés = filtres Shopify sans le préfixe `filter.`, mêmes que ceux utilisés
    // par buildSearchUri() pour la recherche produit.
    const params = {};
    const category = BIKETYPE_CATEGORY[this.state.bikeType];
    if (category) {
      params['p.m.specs.category'] = { name: 'category', value: category };
    }
    const types = this.resolveTypes();
    if (types.length > 0) {
      params['p.m.specs.type'] = { name: 'type', value: types };
    }
    if (this.state.sizeCm) {
      params['v.m.taille_du_cycliste.liste'] = { name: 'taille', value: this.state.sizeCm };
    }
    if (this.state.budget && this.state.budget < 9999) {
      params['v.price'] = { name: 'budget', value: this.state.budget };
    }

    const payload = {
      id: data.id,
      firstName: data.firstname || '',
      lastName: data.lastname || '',
      email: data.email || '',
      phone: '',
      params,
    };

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  // -------------------------------------------------------- restart
  restart() {
    this.state = {
      bikeType: null,
      terrain: null,
      size: null,
      sizeCm: null,
      budget: 3000,
      leadEmail: null,
      leadPhone: null,
    };
    this.leadSubmitted = false;
    this.recomputeFlow();
    this.querySelectorAll('input[type="radio"]').forEach((r) => (r.checked = false));
    this.querySelectorAll('.alert-bikes-modal__vcard').forEach((c) => {
      c.classList.remove('alert-bikes-modal__vcard--selected');
      c.setAttribute('aria-checked', 'false');
    });
    // Budget : ré-sélectionne la carte par défaut (1500 € - 3000 €).
    const defaultBudget = this.querySelector('#budget-3000-modal');
    if (defaultBudget) defaultBudget.checked = true;
    const defaultBudgetCard = this.querySelector('[data-for="budget-3000-modal"]');
    if (defaultBudgetCard) {
      defaultBudgetCard.classList.add('alert-bikes-modal__vcard--selected');
      defaultBudgetCard.setAttribute('aria-checked', 'true');
    }
    const cmInput = this.querySelector('[data-cm-input]');
    if (cmInput) cmInput.value = '';
    const form = this.querySelector('#x-bike-alert');
    if (form) form.reset();
    this.activateStep('intro');
  }

  // -------------------------------------------------------- validation
  validateEmail(input) {
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value);
    if (input.value && !ok) input.classList.add('alert-bikes-modal__fi--error');
    else input.classList.remove('alert-bikes-modal__fi--error');
  }

  // Active le CTA « Créer l'alerte » uniquement quand les 3 champs obligatoires
  // sont valides (prénom + nom non vides, email au bon format). Tant qu'il est
  // disabled, aucun clic n'est émis → ni soumission, ni tracking bike_finder_alert.
  refreshAlertSubmitEnabled() {
    const form = this.querySelector('[data-form-type="alert"]');
    if (!form) return;
    const submitBtn = form.querySelector('.alert-bikes-modal__submit');
    if (!submitBtn) return;
    const fn = form.querySelector('input[name="firstname"]')?.value?.trim();
    const ln = form.querySelector('input[name="lastname"]')?.value?.trim();
    const email = form.querySelector('input[name="email"]')?.value?.trim();
    const emailOk = !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    submitBtn.disabled = !(fn && ln && emailOk);
  }

  // -------------------------------------------------------- fmd
  getFmdContext() {
    if (!window.FmdSession) return { isFmd: false, handle: null, isGeneric: false };
    const handle = window.FmdSession.getMetaobjectHandle();
    const isGeneric = window.FmdSession.isGenericFlow();
    return { isFmd: !!(handle || isGeneric), handle, isGeneric };
  }
}

if (!customElements.get('alert-bikes-modal')) {
  customElements.define('alert-bikes-modal', AlertBikesModal);
}
